import { useEffect, useState, useRef, useMemo } from "react";
import {
  Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Grid
} from "@mui/material";
import RemoveRowButton from "./RemoveRowButton";
import {
  tituloTabelaAddTomadaPorCarga,
  opcoesTabelaAddTomadaPorCarga,
  tiposServicos
} from "../../../static";
import './addTomada.css';

const AddTomadaFaseOne = ({
  tipo,
  tipoCarga,
  tipoServico,
  onDataChange,
  onRemove
}) => {
  const slug = tipo || tipoCarga || tipoServico;

  const isService = useMemo(() => {
    return tiposServicos.some((s) => s.id === slug);
  }, [slug]);

  const colunas = useMemo(() => {
    return tituloTabelaAddTomadaPorCarga[slug] || {};
  }, [slug]);

  const colunasOcultas = useMemo(() => {
    if (isService) {
      return ["coluna14", "coluna15", "coluna16", "coluna18", "coluna19", "coluna20", "coluna21", "coluna22"];
    } else {
      return ["coluna15", "coluna16"];
    }
  }, [isService]);

  const colunasFiltradas = useMemo(() => {
    return Object.fromEntries(
      Object.entries(colunas).filter(([key]) => !colunasOcultas.includes(key))
    );
  }, [colunas, colunasOcultas]);

  const orderedColKeys = useMemo(() => {
    const keys = Object.keys(colunasFiltradas).filter((k) => k !== "coluna14");
    keys.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    if (colunasFiltradas.coluna14) {
      keys.push("coluna14");
    }
    return keys;
  }, [colunasFiltradas]);

  const colunasYesNo = [
    "coluna1", "coluna4", "coluna5", "coluna7", "coluna8", "coluna9", "coluna10", "coluna11"
  ];

  const INSUMOS = {
    Vapor: {
      coluna19: "Vazão (t/h)",
      coluna20: "Diâmetro da tubulação (m)",
      coluna21: "Pressão da tubulação (bar)",
      coluna22: "Consumo (t/Mês)",
    },
    "Ar comprimido": {
      coluna19: "Vazão (m³/h)",
      coluna20: "Diâmetro da tubulação (m)",
      coluna21: "Pressão da tubulação (bar)",
      coluna22: "Consumo (m³/Mês)",
    },
    "Energia Elétrica": {
      coluna22: "Consumo (MWh/Mês)",
    },
    "Gás Natural": {
      coluna19: "Vazão (m³/h)",
      coluna20: "Diâmetro da tubulação (m)",
      coluna21: "Pressão da tubulação (bar)",
      coluna22: "Consumo (m³/Mês)",
    },
    GLP: {
      coluna19: "Vazão (m³/h)",
      coluna20: "Diâmetro da tubulação (m)",
      coluna21: "Pressão da tubulação (bar)",
      coluna22: "Consumo (m³/Mês)",
    },
    Óleo: {
      coluna19: "Vazão (litros/h)",
      coluna20: "Diâmetro da tubulação (m)",
      coluna21: "Pressão da tubulação (bar)",
      coluna22: "Consumo (Litros/Mês)",
    },
  };

  const createEmptyRow = () => {
    const emptyRow = orderedColKeys.reduce((acc, key) => {
      acc[key] = key === "coluna14" ? 0 : "";
      return acc;
    }, {});
    if (isService) {
      emptyRow.coluna14 = 1;
      emptyRow.coluna18 = "";
    }
    return emptyRow;
  };

  const [rows, setRows] = useState(() => [createEmptyRow()]);
  const prevRowsRef = useRef();

  // Reset rows when slug changes
  useEffect(() => {
    setRows([createEmptyRow()]);
  }, [slug]);

  useEffect(() => {
    if (onDataChange && JSON.stringify(rows) !== JSON.stringify(prevRowsRef.current)) {
      onDataChange(rows);
      prevRowsRef.current = [...rows];
    }
  }, [rows, onDataChange]);

  const addRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const handleRowChange = (index, field, value) => {
    const updated = rows.map((row, i) => {
      if (i === index) {
        const updatedRow = { ...row, [field]: value };
        if (isService) {
          updatedRow.coluna14 = 1;
        }
        return updatedRow;
      }
      return row;
    });
    setRows(updated);
  };

  const handleDelete = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    if (isService || updated.length === 0) {
      onRemove?.();
    }
  };

  const handleInsumoChange = (index, insumo) => {
    const updated = rows.map((row, i) =>
      i === index
        ? { ...row, coluna18: insumo, coluna19: "", coluna20: "", coluna21: "", coluna22: "" }
        : row
    );
    setRows(updated);
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 4 }}>
        <Table stickyHeader sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              {orderedColKeys.map((key) => {
                const label = colunasFiltradas[key];
                return label ? (
                  <TableCell key={key} align="center">
                    {label}
                  </TableCell>
                ) : null;
              })}
              {slug === "medicao_de_sistemas_e_insumos" && (
                <>
                  <TableCell align="center">Insumo</TableCell>
                  {rows.length > 0 &&
                    Object.entries(INSUMOS[rows[0].coluna18] || {}).map(([colKey, label]) => (
                      <TableCell key={colKey} align="center">{label}</TableCell>
                    ))}
                </>
              )}
              <TableCell align="center" sx={{ width: "80px", maxWidth: "80px", whiteSpace: "nowrap" }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                {orderedColKeys.map((key) => {
                  const label = colunasFiltradas[key];
                  if (!label) return null;

                  // Render dropdown if options exist in static config
                  const options = opcoesTabelaAddTomadaPorCarga[slug]?.[key];
                  if (options) {
                    return (
                      <TableCell key={key} align="center">
                        <select
                          className="table-select"
                          value={row[key] || ""}
                          onChange={(e) => handleRowChange(index, key, e.target.value)}
                        >
                          <option value="">Selecione</option>
                          {options.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </TableCell>
                    );
                  }

                  // Render dropdown for Yes/No columns
                  const isYesNo = isService ? colunasYesNo.includes(key) : (label.includes("?") || label.includes("S/N"));
                  if (isYesNo) {
                    return (
                      <TableCell key={key} align="center">
                        <select
                          className="table-select"
                          value={row[key] || ""}
                          onChange={(e) => handleRowChange(index, key, e.target.value)}
                        >
                          <option value="">Selecione</option>
                          <option value="Sim">Sim</option>
                          <option value="Não">Não</option>
                        </select>
                      </TableCell>
                    );
                  }

                  // Render textarea for Observations
                  if (key === "coluna17") {
                    return (
                      <TableCell key={key} align="center">
                        <textarea
                          className="table-input"
                          style={{ width: "100%", minHeight: "80px", resize: "both" }}
                          value={row[key] || ""}
                          onChange={(e) => handleRowChange(index, key, e.target.value)}
                        />
                      </TableCell>
                    );
                  }

                  // Standard text or number inputs
                  const isNum = key === "coluna14" || label.toLowerCase().includes("quantidade");
                  return (
                    <TableCell key={key} align="center">
                      <input
                        type={isNum ? "number" : "text"}
                        className="table-input"
                        value={row[key] ?? ""}
                        onChange={(e) => handleRowChange(index, key, e.target.value)}
                      />
                    </TableCell>
                  );
                })}

                {slug === "medicao_de_sistemas_e_insumos" && (
                  <>
                    <TableCell align="center">
                      <select
                        className="table-select"
                        value={row.coluna18 || ""}
                        onChange={(e) => handleInsumoChange(index, e.target.value)}
                      >
                        <option value="">Selecione</option>
                        {Object.keys(INSUMOS).map((ins) => (
                          <option key={ins} value={ins}>{ins}</option>
                        ))}
                      </select>
                    </TableCell>

                    {Object.keys(INSUMOS[row.coluna18] || {}).map((colKey) => (
                      <TableCell key={colKey} align="center">
                        <input
                          type="number"
                          className="table-input"
                          value={row[colKey] || ""}
                          onChange={(e) => handleRowChange(index, colKey, e.target.value)}
                        />
                      </TableCell>
                    ))}
                  </>
                )}

                <TableCell align="center" sx={{ width: "80px", maxWidth: "80px", whiteSpace: "nowrap" }}>
                  <RemoveRowButton setRows={() => handleDelete(index)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={1}>
        <Grid item xs={6}></Grid>
        <Grid item xs={6} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={addRow}
          >
            Adicionar Linha
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default AddTomadaFaseOne;
